// SsoReturnHandler — одноразовая обработка возврата от единого профиля
// экосистемы (`?from=sso`) внутри RouterProvider. Провайдер один: маркеры
// `google` и почтовый флоу удалены вместе с самими способами входа.
//
// AuthProvider находится выше RouterProvider, поэтому не должен менять pathname
// через raw history API: TanStack Router не обязан увидеть такой replaceState.
// Здесь есть router-контекст, значит URL и router-state синхронизируются через navigate().
//
// Финализация входа (invalidate auth.me → broadcast → lastLoginMethod → закрыть
// LoginModal) делегирована useAuthCompleted — общая точка для всех способов.

import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { readAuthReturnUrl } from "@/lib/auth-return-url";
import type { LoginMethod } from "@/lib/last-login-method";
import { useAuthCompleted } from "./use-auth-completed";

/** Маркер `?from=`, который ставит useOauthLogin в callbackURL. */
const RETURN_MARKER = "sso";

/** Способ входа, который пишем в подсказку «прошлый раз». */
const RETURN_METHOD: LoginMethod = "sso-exchange";

function mergeReturnPath(
	currentPathname: string,
	currentSearch: string,
	hash: string,
	returnPath: string,
): string {
	const params = new URLSearchParams(currentSearch);
	params.delete("from");

	if (currentPathname === "/" && returnPath !== "/") {
		const saved = new URL(returnPath, window.location.origin);
		const savedParams = new URLSearchParams(saved.search);
		for (const [key, value] of params) {
			savedParams.set(key, value);
		}
		const qs = savedParams.toString();
		return `${saved.pathname}${qs ? `?${qs}` : ""}${saved.hash || hash}`;
	}

	const qs = params.toString();
	return `${currentPathname}${qs ? `?${qs}` : ""}${hash}`;
}

export function SsoReturnHandler(): null {
	const completeAuth = useAuthCompleted();
	const search = useRouterState({
		select: (s) => s.location.search,
	}) as unknown as Record<string, unknown>;
	const handledRef = useRef(false);

	const from = typeof search.from === "string" ? search.from : null;

	useEffect(() => {
		if (handledRef.current || !from || typeof window === "undefined") return;
		if (from !== RETURN_MARKER) return;
		handledRef.current = true;

		// Путь возврата читаем ДО completeAuth: он же его и «съедает» из localStorage.
		const returnTo = mergeReturnPath(
			window.location.pathname,
			window.location.search,
			window.location.hash,
			readAuthReturnUrl(),
		);
		void completeAuth({ method: RETURN_METHOD, returnTo });
	}, [completeAuth, from]);

	return null;
}
